import React, { memo, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FaSpinner } from 'react-icons/fa';
import _ from 'lodash';
import PopupSurveyModal from '../PopupSurveyModal'

import history from '../../utils/history';
import { navigateToCourse } from '../../utils/courseNavigation';
import Tooltip from '../Tooltip';
import NavBarSuperior from '../NavBarSuperior/NavBarSuperior';

import InfoHome from './InfoHome';
import LaneNewGroups from './LaneNewGroups';
import LaneBooks from './LaneBooks';
import LaneCourses from './LaneCourses';
import LaneEvents from './LaneEvents';
import LaneWarnings from './LaneWarnings';
import LaneList from './LaneList';

import CustomContentLoader from './CustomContentLoader';
import { gateway } from '../../utils/api';
import {
  canRedirectToTurma,
  classesRetryDelay,
  ensureSelectedClass,
  fetchMyClasses,
  getClassesQueryKey,
  notifyRedirectLoop,
  redirectToTurma
} from '../../utils/turmaRedirect';
import { WrapperInfiniteScroll, LoadingWrapper, BodyDinamicBackgroundAndTextColor } from './styles';

import { Creators as CourseActions } from '../../store/ducks/newCourse/actions';
import { Creators as StudyGroupActions } from '../../store/ducks/user/studyGroup/actions';
import { CheckAddressOnboarding } from '../CheckAddressOnboarding';

const lanesComponentsDictionary = {
  LANE_COURSES: LaneCourses,
  LANE_IN_PROGRESS: LaneCourses,
  LANE_CLASS_COURSE: LaneCourses,
  LANE_LAST_VIEWED: LaneCourses,
  LANE_BOOKS: LaneBooks,
  LANE_BOOKS_DEFAULT: LaneBooks,
  LANE_SOCIAL_GROUPS: LaneNewGroups,
  LANE_EVENTS: LaneEvents,
  LANE_WARNINGS: LaneWarnings,
  LANE_CLASS: LaneList,
  LANE_TOPICS_BY_INTEREST: LaneList,
  LANE_TOP_COURSES_WATCHED: LaneCourses,
  LANE_MANDATORY_COURSES: LaneCourses,
};

const fetchHomeData = async () => {
  const homeCrCode = localStorage.getItem('homeCrCode');
  const defaultHomeCrCode = localStorage.getItem('defaultHomeCrCode');

  const url = homeCrCode
    ? `webbff/lit_app_web/home/v2/?homeCrCode=${homeCrCode}`
    : 'webbff/lit_app_web/home/v2';

  const response = await gateway.get(url);

  if (!defaultHomeCrCode) {
    localStorage.setItem('defaultHomeCrCode', response.homeCode);
  }

  return _.get(response, 'lanes', []);
};

const getSettings = () => {
  try {
    const raw = localStorage.getItem("settings");
    if (raw == null || raw === "undefined") return null;
    return JSON.parse(raw) || null;
  } catch {
    return null;
  }
};

const Home = () => {
  const settings = getSettings();
  const tenant = localStorage.getItem("tenant") || "";
  const homeCrCode = localStorage.getItem("defaultHomeCrCode") || "";
  const selectedClassCrCode = localStorage.getItem("selectedClassCrCode") || "";
  const crCodeProfile = localStorage.getItem("crCodeProfile") || "";
  const [lanesComponents, setLanesComponents] = useState([]);
  const [showcaseBanners, setShowcaseBanners] = useState([]);
  const [items, setItems] = useState([]);
  const [loader, setLoader] = useState(null);
  const idProfile = localStorage.getItem('idProfile');

  const dispatch = useDispatch();
  const redirectToTenantData = useSelector(state => state.newCourse.redirectToTenantData);
  const myClasses = useSelector(state => _.get(state, 'user.studyGroup.myClasses', []));
  const redirectedToNewRef = useRef(false);
  const [redirectBlocked, setRedirectBlocked] = useState(false);

  // Chave inclui tenant e perfil: o idProfile é o mesmo para todos os perfis do usuário,
  // então sem isso as turmas de um perfil eram reaproveitadas em outro.
  // Sem cache (staleTime/cacheTime 0): a decisão sempre usa a resposta atual do /classes.
  const {
    data: myClassesData,
    isLoading: isLoadingMyClasses,
    isError: isMyClassesError
  } = useQuery(getClassesQueryKey(), fetchMyClasses, {
    enabled: Boolean(idProfile),
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: classesRetryDelay,
    staleTime: 0,
    cacheTime: 0,
    onSuccess: data => {
      dispatch(StudyGroupActions.setMyClasses(data));
    }
  });

  // Decisão "tem turma?" ANTES de buscar/renderizar a home:
  // - tem turma  -> redireciona direto para /turma (não chama o endpoint da home).
  // - não tem    -> carrega a home normalmente.
  const isOnTurma =
    typeof window !== 'undefined' &&
    window.location &&
    typeof window.location.pathname === 'string' &&
    window.location.pathname.indexOf('/turma') === 0;

  // Fonte da verdade é a resposta atual do /classes. O Redux (persistido / atualizado pelo
  // NavBarSuperior) só é usado como fallback quando o /classes falhou mesmo após o retry.
  const classesToCheck = isMyClassesError ? myClasses : myClassesData;
  const hasTurma = Array.isArray(classesToCheck) && classesToCheck.length > 0;
  // Em react-query v3 uma query desabilitada fica "idle" (isLoading = false),
  // então isLoadingMyClasses só é true enquanto realmente busca as turmas.
  const classesKnown = !isLoadingMyClasses;
  const willRedirectToTurma = !isOnTurma && hasTurma && !redirectBlocked;
  // Só busca a home quando já sabemos que o usuário NÃO tem turma.
  const shouldFetchHome = classesKnown && !willRedirectToTurma;

  const { data: lanes, isLoading } = useQuery(`homeData-${idProfile}-${tenant}-${crCodeProfile}-${homeCrCode}-${selectedClassCrCode}`, fetchHomeData, {
    enabled: shouldFetchHome,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 1000 * 60 * 10,
    cacheTime: 1000 * 60 * 30,
  });

  const getLaneFromObj = obj => {
    const type = _.get(obj, 'lane.type', null);

    if (type === 'VITRINE_DISPLAY') {
      const filtredDocsToWeb = obj.docs.filter(doc => doc.tags.includes('WEB'));
      setShowcaseBanners(filtredDocsToWeb);
    }

    const component = lanesComponentsDictionary[type];
    return component ? { component, show: true, data: obj } : null;
  };

  useEffect(() => {
    if (lanes) {
      setLanesComponents(lanes.map(getLaneFromObj).filter(Boolean));
    }
  }, [lanes]);



  const getAdditionalLanes = () => {
    const marginTop = 500;
    const marginBottom = 245;
    const lanesMinHeight = 124;
    const remainingSpace = window.innerHeight - marginTop - marginBottom;
    return Math.trunc(remainingSpace / lanesMinHeight);
  };

  const lanesMemoized = useMemo(() => {
    const lanesItems = [...lanesComponents];
    setItems(lanesItems.slice(0, 6 + getAdditionalLanes()));
    return lanesItems;
  }, [lanesComponents]);

  const getLoader = () => {
    if (isLoading) {
      return (
        <>
          {[...Array(2)].map((_, i) => (
            <CustomContentLoader key={i} tooltipText="Carregando descrição" title="Carregando Lista" />
          ))}
        </>
      );
    }

    if (items.length !== lanesMemoized.length) {
      return (
        <LoadingWrapper>
          <FaSpinner size={20} color="#27aae1" />
        </LoadingWrapper>
      );
    }
    return null;
  };

  const fetchMoreData = useCallback(() => {
    const moreItems = lanesMemoized.slice(items.length, items.length + 3);

    if (moreItems.length > 0) {
      setTimeout(() => {
        setItems(prevState => prevState.concat(moreItems));
        setLoader(getLoader());
      }, 1500);
    } else {
      setItems(prevState => prevState.concat(items.length));
    }
  }, [items]);

  useEffect(() => {
    setLoader(getLoader());
  }, [isLoading]);

  useEffect(() => {
    if (redirectToTenantData) {
      const { course, object, isSelectedTenant } = redirectToTenantData;
      if (isSelectedTenant && course) {
        dispatch(CourseActions.redirectToTenant(null));
        navigateToCourse(course, undefined, { history, object });
      }
    }
  }, [redirectToTenantData]);

  useEffect(() => {
    if (redirectedToNewRef.current) return;
    if (isOnTurma) return;

    // Aguarda saber se o usuário tem turma antes de decidir.
    if (!classesKnown) return;
    if (!hasTurma || redirectBlocked) return;

    // Anti-loop: se o /turma devolver o usuário para cá várias vezes em sequência,
    // para de redirecionar e mostra a home (em vez de ficar preso no loader).
    if (!canRedirectToTurma()) {
      notifyRedirectLoop('home');
      setRedirectBlocked(true);
      return;
    }

    ensureSelectedClass(classesToCheck);
    redirectedToNewRef.current = true;
    redirectToTurma();
  }, [classesToCheck, isLoadingMyClasses, redirectBlocked]);

  // Enquanto decidimos (turmas carregando) ou vamos redirecionar para /turma,
  // não renderiza a home nem chama o endpoint dela — apenas um loader curto.
  if (!shouldFetchHome) {
    return (
      <LoadingWrapper
        style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <FaSpinner size={24} color="#27aae1" />
      </LoadingWrapper>
    );
  }

  return (
    <div id="home">
      <CheckAddressOnboarding />
      <Tooltip id="home" />
      <NavBarSuperior home reviewSmartBar />

      <BodyDinamicBackgroundAndTextColor
        backgroundColor={_.get(settings, `${tenant}.bodyBackgroundColor`, "")}
        textColor={_.get(settings, `${tenant}.bodyTextColor`, "")}
        style={{ overflowX: 'hidden', minHeight: '100vh', paddingBottom: 50 }}
      >
        <InfoHome showcaseBanners={showcaseBanners} />
        <WrapperInfiniteScroll>
          <InfiniteScroll
            dataLength={items.length}
            next={fetchMoreData}
            hasMore
            loader={loader}
            scrollableTarget="scrollableDiv"
            scrollThreshold={0.5}
          >
            {!isLoading &&
              items.map((item, index) =>
                item.component ? <item.component key={index} data={item.data} /> : null
              )}
          </InfiniteScroll>
        </WrapperInfiniteScroll>
      </BodyDinamicBackgroundAndTextColor>
      <PopupSurveyModal />
    </div>
  );
};

export default memo(Home);
